import { useEffect, useRef, useState } from "react";
import { ChevronDown, Globe } from "lucide-react";
import { LANGS, useI18n } from "@/lib/i18n";

export const LanguageSwitcher = ({ compact = false, testIdPrefix = "lang" }) => {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.switch_label")}
        title={t("lang.switch_label")}
        className={`inline-flex items-center gap-1.5 rounded-[5px] border border-[color:var(--gv-line)] text-[color:var(--gv-ink)] hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] transition-colors ${compact ? "px-2.5 py-2 text-[13px]" : "px-3 py-2.5 text-[13.5px]"}`}
        data-testid={`${testIdPrefix}-switcher-toggle`}
      >
        {compact ? <span aria-hidden className="text-[15px] leading-none">{current.flag}</span> : (
          <>
            <Globe size={14} />
            <span aria-hidden>{current.flag}</span>
            <span className="uppercase">{current.code}</span>
          </>
        )}
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 w-44 bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-[0_24px_60px_-20px_rgba(18,24,32,0.25)] overflow-hidden z-[60]"
          data-testid={`${testIdPrefix}-switcher-menu`}
        >
          {LANGS.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === lang}>
              <button
                type="button"
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-[13.5px] hover:bg-[color:var(--gv-mist)] ${l.code === lang ? "text-[color:var(--gv-pink)] font-medium" : "text-[color:var(--gv-ink)]"}`}
                data-testid={`${testIdPrefix}-switcher-${l.code}`}
                title={l.label}
                aria-label={l.label}
              >
                <span aria-hidden className="text-[15px] leading-none">{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                <span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/50">{l.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
